// Reading a rubric verdict, in one place.
//
// Two rubrics are in circulation and a paper keeps whichever one scored it, so
// every screen that shows a score has to handle both shapes. These helpers were
// defined inside Review.jsx and copied, differently and incompletely, into
// AnalyzeQuestionPapers.jsx - which is how that page came to crash on a 0-4
// verdict by calling .split() on a number.
//
// The whole review section reads them from here. AnalyzeQuestionPapers still
// carries its own copy, patched separately; folding it in is worth doing but is
// a change to a different page, so it is not done here.

// Bloom's taxonomy levels. The analysis stores the level as an integer at
// question-analysis level and as a NAME on the question row, because the page
// walk reports the name; both appear, so both are mapped.
export const BLOOM_NAMES = {
  1: "Remember",
  2: "Understand",
  3: "Apply",
  4: "Analyse",
  5: "Evaluate",
  6: "Create",
};

// Accepts either spelling of level 4 - the rubric says "Analyze", the UI says
// "Analyse" - because the stored value is whatever the model wrote.
const BLOOM_NUMBERS = {
  remember: 1,
  understand: 2,
  apply: 3,
  analyse: 4,
  analyze: 4,
  evaluate: 5,
  create: 6,
};

export const bloomNumber = (name) => {
  if (typeof name === "number") return Number.isInteger(name) ? name : null;
  const key = String(name || "").trim().toLowerCase();
  return BLOOM_NUMBERS[key] || null;
};

// A criterion verdict arrives in one of two shapes, because two rubrics are in
// play and a paper keeps whichever one scored it:
//
//   0-3 rubric  {score: "2/3", justification}          - score is a STRING "n/m"
//   0-4 rubric  {score: 3, max: 4, label, issues, ...} - score is a NUMBER
//
// Both are read here rather than converted on the way out of the API, because
// the stored rows genuinely differ and rewriting one to look like the other
// would throw away the fields only the newer one has. A verdict with no usable
// score at all returns null, which renders as unscored rather than as zero.
export const parseScore = (verdict) => {
  if (verdict == null) return null;
  const raw = verdict.score;
  if (typeof raw === "number" && Number.isFinite(raw)) {
    const max = Number(verdict.max);
    if (!Number.isFinite(max) || max <= 0) return null;
    return { value: raw, max, ratio: raw / max };
  }
  const match = /^\s*([\d.]+)\s*\/\s*([\d.]+)\s*$/.exec(String(raw ?? ""));
  if (!match) return null;
  const value = Number(match[1]);
  const max = Number(match[2]);
  if (!Number.isFinite(value) || !Number.isFinite(max) || max <= 0) return null;
  return { value, max, ratio: value / max };
};

// What to print in the pill. The 0-4 rubric stores the number and its maximum
// separately, so "3" alone would read as three out of nothing.
export const scoreText = (verdict, score) => {
  if (verdict?.skipped) return "n/a";
  if (!score) return "—";
  return `${score.value}/${score.max}`;
};

// Three bands rather than a continuous gradient: the scores underneath are small
// integers - out of 3 on the older rubric, out of 4 on the newer one - so a
// gradient would imply precision the data lacks. Full marks is "good"; the older
// rubric's 2/3 and the newer one's 3/4 both land in "fair", which is the right
// reading of "acceptable but not strong" on either scale.
export const bandFor = (ratio) =>
  ratio == null ? "unknown" : ratio >= 0.999 ? "good" : ratio >= 0.6 ? "fair" : "poor";

// Criterion names arrive in two shapes: the local rubric writes them in prose
// ("Alignment to CLOs"), the bulk rubric in snake_case ("marks_vs_effort"). Only
// the second needs rewriting - a blanket CSS capitalize handled it but turned
// the first into "Alignment To CLOs", and mid-sentence words should stay lower
// case while an acronym keeps its own casing.
export const criterionLabel = (name) =>
  name.includes("_")
    ? name
        .split("_")
        .map((word, index) =>
          index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word)
        .join(" ")
    : name;

// The per-question quality score's own thresholds, not bandFor's. bandFor is
// built for the rubric's integers out of 3, where anything below full marks is a
// real deduction, so reusing it put 8/10 and 9/10 in the same middling band as
// 6/10 - almost every question in the catalogue read as mediocre. On a
// ten-point quality score an eight is good.
export const qualityBand = (value) =>
  value >= 8 ? "good" : value >= 5.5 ? "fair" : "poor";

// How a question's criteria came out, as three counts that mean three different
// things:
//
//   scored   - a real judgement, counts toward both total and max
//   unscored - the model was ASKED and returned nothing usable. It still counts
//              toward max, so it deflates the percentage exactly like a real
//              zero would, for a reason that has nothing to do with the paper.
//              This is why it needs saying out loud wherever a percentage is
//              shown - the number is understated and looks like a weakness.
//   skipped  - never asked, because the course outcomes it depends on are
//              unknown. Counts toward neither: the denominator shrinks from 28
//              to 24, which is the honest arithmetic.
export const criteriaSummary = (question) => {
  const unscored = Array.isArray(question?.unscored) ? question.unscored.length : 0;
  const skippedList = question?.skipped;
  const skipped = Array.isArray(skippedList)
    ? skippedList.length
    : skippedList && typeof skippedList === "object"
      ? Object.keys(skippedList).length
      : 0;
  const verdicts = Object.values(question?.rubric_assessment || {});
  // Fall back to the verdicts themselves where the summary columns are null,
  // which is every question the older engines scored.
  const derivedSkipped = verdicts.filter((v) => v?.skipped).length;
  const derivedUnscored = verdicts.filter(
    (v) => v && !v.skipped && parseScore(v) === null).length;
  return {
    total: verdicts.length,
    unscored: unscored || derivedUnscored,
    skipped: skipped || derivedSkipped,
  };
};

// A question's score as a percentage of what it could have scored. Read from the
// stored pair when it is there, because criteria_max is the denominator the
// scorer actually used and it shrinks when a criterion is skipped; derived from
// the verdicts otherwise.
export const questionPct = (question) => {
  const total = question?.criteria_total;
  const max = question?.criteria_max;
  if (Number.isFinite(total) && Number.isFinite(max) && max > 0) {
    return (100 * total) / max;
  }
  let sum = 0;
  let top = 0;
  Object.values(question?.rubric_assessment || {}).forEach((verdict) => {
    if (verdict?.skipped) return;
    const score = parseScore(verdict);
    if (!score) return;
    sum += score.value;
    top += score.max;
  });
  return top > 0 ? (100 * sum) / top : null;
};

// The number a reviewer can match against the document on screen. Papers that
// divide into parts restart numbering at 1, so a printed number alone is
// ambiguous and the part has to travel with it.
export const questionLabel = (question) => {
  const printed = question?.printed_number;
  const part = question?.part_label;
  if (printed && part) return `${part} · Q${printed}`;
  if (printed) return `Q${printed}`;
  return `Q${question?.question_number ?? "?"}`;
};

// A paper that scored nothing at all, as distinct from a paper that scored
// badly. The distinction has to be made: a row of zeros reads as "the worst
// paper here" about a document nobody managed to read. It happens when every
// page fails while being read - the walk finishes normally, having found
// nothing, and a completed analysis of nothing is stored.
export const notEvaluated = (paper) =>
  Boolean(paper?.scored) && (paper.engine_questions === 0
    || (paper.analyzed_questions === 0 && paper.final_weighted_score == null));

// The paper-level rubric score as a percentage. The denominator is stored
// rather than derived because it shrinks when a criterion is skipped for want of
// course outcomes, so 19/28 and 19/24 are different results.
export const paperRubricPct = (paper) => {
  // Both halves, and null-checked BEFORE the numeric coercion. Number(null) is
  // 0, which is perfectly finite - so checking only Number.isFinite would turn
  // a paper carrying a maximum with no total, which is what a paper-level run
  // that died halfway leaves behind, into a confident 0%. A missing total is
  // "not measured", never nought.
  const { paper_total: total, paper_max: max } = paper || {};
  if (total == null || max == null) return null;
  const t = Number(total);
  const m = Number(max);
  if (!Number.isFinite(t) || !Number.isFinite(m) || m <= 0) return null;
  return (100 * t) / m;
};
